import HelpOutlineRoundedIcon from '@mui/icons-material/HelpOutlineRounded';
import { Avatar, Box, CardContent, Tooltip, Typography } from '@mui/material';

// Define the structure of the props expected by the InfoBlock component
interface InfoBlockParams {
  /** A unique base identifier for accessibility and testing purposes */
  baseId: string;

  /** The main title text displayed at the top of the InfoBlock */
  title: string;

  /** A subtitle providing additional context or information */
  subtitle: string;

  /** The icon to display inside the Avatar */
  icon: JSX.Element;

  /** The primary content or data to display alongside the Avatar */
  content: string;

  /** The background color for the Avatar, typically a theme color */
  color: string;
}

/**
 * InfoBlock Component
 *
 * A reusable UI component that displays a titled block with an avatar, primary content,
 * and a subtitle. It is styled using Material-UI components and supports customization
 * through props.
 *
 * @param props - The properties to configure the InfoBlock
 * @returns A styled CardContent element containing the InfoBlock content
 */
const InfoBlock = (props: InfoBlockParams) => {
  return (
    <CardContent
      sx={{
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        paddingX: '24px',
        paddingY: '20px',
      }}
    >
      <Box display="flex" justifyContent="space-between" alignItems="center">
        {/* Title Section */}
        <Typography
          id={`${props.baseId}-info-block-title`} // Unique ID for accessibility/testing
          sx={{
            fontSize: '13px',
            fontWeight: 500,
            textTransform: 'uppercase',
            lineHeight: 2.66,
          }}
        >
          {props.title}
        </Typography>

        <Tooltip title={props.subtitle} arrow>
          <HelpOutlineRoundedIcon
            sx={{
              cursor: 'help',
            }}
          />
        </Tooltip>
      </Box>

      {/* Content Section: Avatar and Primary Content */}
      <Box height="100%" display="flex" alignItems="center" marginY="16px">
        {/* Avatar Display */}
        <Avatar
          sx={{
            width: '50px',
            height: '50px',
            bgcolor: props.color,
            color: 'white',
            boxShadow: `${props.color}40 0px 1px 4px, ${props.color}59 0px 3px 12px 2px`, // Shadow with varying opacity
          }}
        >
          {props.icon}
        </Avatar>

        {/* Primary Content Display */}
        <Typography
          id={`${props.baseId}-info-block-data`} // Unique ID for accessibility/testing
          sx={{
            marginLeft: '16px',
            fontWeight: 500,
            fontSize: '30px',
          }}
        >
          {props.content}
        </Typography>
      </Box>
    </CardContent>
  );
};

export default InfoBlock;
